import { Box, ListItemButton } from '@mui/material';
import clsx from 'clsx';
import React, {
  Children,
  isValidElement,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
  useEffect,
} from 'react';
import { useMenuContext } from './MenuContext';
import { useMenuGroupContext } from './MenuGroupContext';
import { useInnerText } from '@client/hooks/use-inner-text.hook';
import { guards } from '@shared/helpers/typeguards.helpers';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import { MenuItemAction } from './MenuItemAction';
import { MenuItemRightIcon } from './MenuItemRightIcon';
import { makeStyles } from 'tss-react/mui';
import { MENU_ITEM_STYLE, SUBMENU_TIMEOUTS } from './menu.model';
import { EllipsisText } from '@ui/EllipsisText';
import { Tooltip } from '@ui/Tooltip';

export type BaseMenuItemProps = {
  onClick?: () => void;
  /** True or string disables the item; non-empty string shows as disabled tooltip */
  disabled?: boolean | string;
  icon?: ReactNode;
  children: ReactNode;
  closeOnClick?: boolean;
  'data-menu-index'?: number;
  /** Text to search against. If not provided, DOM node text content will be used */
  searchText?: string;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
  /** Callback invoked when the item is registered with the menu, providing the index and element */
  onRegistered?: (index: number, element: HTMLDivElement) => void;
  /** Force highlight state independent of focus tracking (e.g. when submenu is open) */
  highlighted?: boolean;
  /** Selected state - used to indicate an item is selected (for example for select implementations) */
  selected?: boolean;
  'aria-pressed'?: boolean;
};

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
];
const FOCUSABLE_SELECTOR = FOCUSABLE_SELECTORS.join(',');

const LABEL_BOX = {
  HEIGHT: 28,
} as const;

const MENU_SEARCH_SELECTOR = '[data-menu-search]';
/** Matches the menu list only while its own search field holds focus. */
const MENU_SEARCH_FOCUSED_SELECTOR = `ul:has(${MENU_SEARCH_SELECTOR}:focus)`;

const isMenuSearchFocused = () =>
  document.activeElement?.matches(MENU_SEARCH_SELECTOR) ?? false;

const useStyles = makeStyles<{ selected: boolean }, 'trailingCell'>()(
  (theme, { selected }, classes) => ({
    root: {
      display: 'grid',
      gridTemplateColumns: `${MENU_ITEM_STYLE.ICON_COLUMN_WIDTH}px 1fr auto`,
      alignItems: 'center',
      columnGap: theme.spacing(MENU_ITEM_STYLE.COLUMN_GAP),
      paddingLeft: theme.spacing(MENU_ITEM_STYLE.PADDING_X),
      paddingRight: theme.spacing(MENU_ITEM_STYLE.PADDING_X),
      paddingTop: theme.spacing(MENU_ITEM_STYLE.PADDING_Y),
      paddingBottom: theme.spacing(MENU_ITEM_STYLE.PADDING_Y),
      '&:hover': {
        backgroundColor: 'transparent', // disable default hover effect - hover style is handled by focus state
      },
      // While the search field holds focus, items never take focus on hover, so
      // hover has to paint the highlight itself.
      [`${MENU_SEARCH_FOCUSED_SELECTOR} &:hover`]: {
        backgroundColor: theme.palette.neutral.superLight,
        [`& .${classes.trailingCell} [data-menu-trailing="action"]`]: {
          opacity: 1,
        },
      },
      // Override MUI's combined selected + keyboard-focus primary tint too.
      '&&.Mui-focusVisible, &&:focus-within, &&.Mui-selected': {
        backgroundColor: theme.palette.neutral.superLight,
        '&:hover': {
          backgroundColor: theme.palette.neutral.superLight,
          // this is to override default hover effect on selected item
        },
        [`& .${classes.trailingCell} [data-menu-trailing="action"]`]: {
          opacity: 1,
        },
      },
      '&:active': {
        backgroundColor: theme.palette.neutral.lightest,
        boxShadow: `inset 0 0 4px 0px ${theme.palette.neutral.lighter}`,
        [`& .${classes.trailingCell} [data-menu-trailing="action"]`]: {
          opacity: 1,
        },
      },
    },
    iconSlot: {
      width: MENU_ITEM_STYLE.ICON_COLUMN_WIDTH,
      minWidth: MENU_ITEM_STYLE.ICON_COLUMN_WIDTH,
      color: theme.palette.text[selected ? 'primary' : 'subtle'],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    iconPlaceholder: {
      minHeight: LABEL_BOX.HEIGHT,
      display: 'inline-block',
    },
    labelCell: {
      minWidth: 0,
      display: 'flex',
      alignItems: 'center',
    },
    labelText: {
      display: 'inline-flex',
      minHeight: LABEL_BOX.HEIGHT,
      maxWidth: '100%',
      lineHeight: `${LABEL_BOX.HEIGHT}px`,
      color: selected ? theme.palette.text.primary : undefined,
      fontWeight: selected ? theme.typography.fontWeightMedium : undefined,
    },
    trailingCell: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      minWidth: MENU_ITEM_STYLE.ICON_COLUMN_WIDTH,
      '& [data-menu-trailing="action"]': {
        opacity: 0,
        transition: 'opacity 0.2s ease-in-out',
      },
    },
    tooltipTrigger: {
      display: 'block',
      width: '100%',
    },
  }),
);

const TRAILING_DISPLAY_NAMES = new Set([
  MenuItemAction.displayName,
  MenuItemRightIcon.displayName,
]);

const isTrailingChild = (child: ReactNode): boolean => {
  if (!isValidElement(child)) return false;
  const childType = child.type;
  if (childType === MenuItemAction || childType === MenuItemRightIcon)
    return true;
  if (!guards.is.string(childType)) {
    const descriptor = childType as { displayName?: unknown; name?: unknown };
    const maybeDisplayName = descriptor.displayName ?? descriptor.name;
    const displayName = guards.is.string(maybeDisplayName)
      ? maybeDisplayName
      : undefined;
    return displayName ? TRAILING_DISPLAY_NAMES.has(displayName) : false;
  }
  return false;
};

const BaseMenuItem = React.forwardRef<HTMLDivElement, BaseMenuItemProps>(
  (
    {
      onClick,
      disabled = false,
      icon,
      children,
      closeOnClick = true,
      'data-menu-index': providedIndex,
      searchText,
      onMouseEnter,
      onMouseDown,
      onMouseLeave,
      onRegistered,
      highlighted,
      selected = false,
      'aria-pressed': ariaPressed,
    },
    forwardedRef,
  ) => {
    const {
      closeAllMenus,
      registerItem,
      unregisterItem,
      registerItemVisibility,
      matchesSearch,
      focusedIndex,
      closeOtherSubmenus,
      isOpen: menuIsOpen,
    } = useMenuContext();
    const groupContext = useMenuGroupContext();
    const indexRef = useRef<number | null>(null);
    const textRef = useRef<HTMLDivElement | null>(null);
    const itemElementRef = useRef<HTMLDivElement | null>(null);
    const innerText = useInnerText(textRef, { observe: true });
    const trailingContainerRef = useRef<HTMLDivElement | null>(null);
    const { classes } = useStyles({ selected });
    const closeTimeoutRef = useRef<NodeJS.Timeout>(undefined);

    const textToSearch = useMemo(() => {
      const childrenText = guards.is.string(children) ? children : '';
      return (searchText ?? innerText ?? childrenText).toLowerCase();
    }, [searchText, innerText, children]);

    const setItemRef = useCallback(
      (ref: HTMLDivElement | null) => {
        itemElementRef.current = ref;
        if (ref && indexRef.current === null) {
          const index = registerItem(ref, providedIndex);
          indexRef.current = index;
          // Register with parent context (group/submenu) or root
          groupContext?.registerChildIndex(index);
          // Notify parent component of registration
          onRegistered?.(index, ref);
          // Forward ref if provided
          if (typeof forwardedRef === 'function') {
            forwardedRef(ref);
          } else if (forwardedRef && 'current' in forwardedRef) {
            forwardedRef.current = ref;
          }
        } else if (!ref) {
          if (indexRef.current !== null) {
            const index = indexRef.current;
            unregisterItem(index);
            // Unregister from parent context (group/submenu) or root
            groupContext?.unregisterChildIndex(index);
            indexRef.current = null;
          }
          if (typeof forwardedRef === 'function') {
            forwardedRef(null);
          } else if (forwardedRef && 'current' in forwardedRef) {
            forwardedRef.current = null;
          }
          itemElementRef.current = null;
        }
      },
      [
        providedIndex,
        registerItem,
        unregisterItem,
        forwardedRef,
        groupContext,
        onRegistered,
      ],
    );

    const handleClick = useCallback(
      (event?: React.SyntheticEvent) => {
        event?.stopPropagation();
        event?.preventDefault();
        if (disabled) return;
        if (closeOnClick) closeAllMenus();
        setTimeout(() => onClick?.(), 50);
      },
      [disabled, onClick, closeOnClick, closeAllMenus],
    );

    const getFocusableNodes = useCallback(() => {
      const nodes: HTMLElement[] = [];
      if (itemElementRef.current) nodes.push(itemElementRef.current);
      if (trailingContainerRef.current) {
        const focusableNodes =
          trailingContainerRef.current.querySelectorAll<HTMLElement>(
            FOCUSABLE_SELECTOR,
          );
        nodes.push(...focusableNodes);
      }
      return nodes;
    }, []);

    const moveFocus = useCallback(
      (direction: 'next' | 'prev', currentNode: HTMLElement | null) => {
        const nodes = getFocusableNodes();
        if (nodes.length === 0) return false;
        if (!currentNode) {
          const targetIndex = direction === 'next' ? 0 : nodes.length - 1;
          const target = nodes[targetIndex];
          if (!target) return false;
          target.focus();
          return true;
        }
        const currentIndex = nodes.indexOf(currentNode);
        if (currentIndex === -1) return false;
        const delta = direction === 'next' ? 1 : -1;
        const nextIndex = (currentIndex + delta + nodes.length) % nodes.length;
        const nextNode = nodes[nextIndex];
        if (!nextNode || nextNode === currentNode) return false;
        nextNode.focus();
        return true;
      },
      [getFocusableNodes],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === KeyboardKeys.ARROW_RIGHT && !disabled) {
          const movedFocus = moveFocus('next', itemElementRef.current);
          if (movedFocus) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }
      },
      [moveFocus, disabled],
    );

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        if (disabled) return;
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = setTimeout(
          () => closeOtherSubmenus(indexRef.current),
          SUBMENU_TIMEOUTS.CLOSE,
        );
        // Skip self-focus when the menu is no longer open. Layout reflows
        // triggered by the action a menu item just dispatched (e.g. Rename
        // mounting an input) can cause the browser to re-fire mouseEnter on
        // a still-mounted-but-closing item, which would otherwise steal focus
        // back from the newly-mounted input.
        // Leave focus in the search field so typing keeps filtering; hover is
        // highlighted via CSS instead.
        if (menuIsOpen && !isMenuSearchFocused()) {
          e.currentTarget.focus();
        }
        onMouseEnter?.(e);
      },
      [disabled, closeOtherSubmenus, onMouseEnter, menuIsOpen],
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        clearTimeout(closeTimeoutRef.current);
        onMouseLeave?.(e);
      },
      [closeTimeoutRef, onMouseLeave],
    );

    useEffect(() => clearTimeout(closeTimeoutRef.current), [closeTimeoutRef]);

    const handleTrailingClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      },
      [],
    );

    const handleTrailingMouseDown = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
      },
      [],
    );

    const handleTrailingKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (
          event.key === KeyboardKeys.ARROW_LEFT ||
          event.key === KeyboardKeys.ARROW_RIGHT
        ) {
          event.preventDefault();
          event.stopPropagation();
          const target =
            event.target instanceof HTMLElement ? event.target : null;
          moveFocus(
            event.key === KeyboardKeys.ARROW_RIGHT ? 'next' : 'prev',
            target,
          );
        }
      },
      [disabled, moveFocus],
    );

    const shouldShow = useMemo(() => {
      if (!textToSearch) return true;
      return matchesSearch(textToSearch);
    }, [textToSearch, matchesSearch]);
    const disabledTooltip =
      guards.is.string(disabled) && guards.is.nonEmptyString(disabled)
        ? disabled
        : false;

    // Register visibility state - bubbles up through parent contexts
    useEffect(() => {
      if (indexRef.current !== null) {
        // Register with nearest parent context (group/submenu) which bubbles up, or root if no parent
        const register =
          groupContext?.registerItemVisibility ?? registerItemVisibility;
        register(indexRef.current, shouldShow);
      }
    }, [shouldShow, registerItemVisibility, groupContext]);

    const childArray = Children.toArray(children);
    const firstTrailingIndex = childArray.findIndex(isTrailingChild);
    const labelCutoff =
      firstTrailingIndex === -1 ? childArray.length : firstTrailingIndex;
    const labelNodes = childArray.slice(0, labelCutoff);
    const trailingNodes = childArray.slice(labelCutoff);
    const hasTrailingContent = trailingNodes.length > 0;
    const trailingContainsAction = trailingNodes.some(
      (child) => isValidElement(child) && child.type === MenuItemAction,
    );
    const trailingAriaHidden =
      hasTrailingContent && !trailingContainsAction ? true : undefined;
    const normalizedTrailingNodes = trailingNodes;

    const isFocused =
      highlighted ??
      (indexRef.current !== null && indexRef.current === focusedIndex);

    if (!shouldShow) return null;

    // non-boolean always means disabled
    const isDisabled = guards.is.boolean(disabled) ? disabled : true;

    const menuItem = (
      <ListItemButton
        ref={setItemRef}
        disabled={isDisabled}
        selected={isFocused}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseDown={onMouseDown}
        className={clsx(classes.root)}
        data-menu-index={indexRef.current ?? undefined}
        aria-pressed={ariaPressed}
        data-menu-selected={selected ? 'true' : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        disableRipple
        disableTouchRipple
      >
        <Box className={classes.iconSlot}>
          {icon ?? (
            <span className={classes.iconPlaceholder} aria-hidden="true" />
          )}
        </Box>
        <div ref={textRef} className={classes.labelCell}>
          <span className={classes.labelText}>
            <EllipsisText tooltipTitle={innerText ?? undefined}>
              {labelNodes}
            </EllipsisText>
          </span>
        </div>
        <Box
          className={classes.trailingCell}
          ref={trailingContainerRef}
          aria-hidden={trailingAriaHidden}
          onClick={handleTrailingClick}
          onMouseDown={handleTrailingMouseDown}
          onKeyDown={handleTrailingKeyDown}
        >
          {normalizedTrailingNodes}
        </Box>
      </ListItemButton>
    );

    if (!disabledTooltip) return menuItem;

    return (
      <Tooltip title={disabledTooltip}>
        <Box component="span" className={classes.tooltipTrigger}>
          {menuItem}
        </Box>
      </Tooltip>
    );
  },
);

BaseMenuItem.displayName = 'BaseMenuItem';

export { BaseMenuItem };
